/**
 * Vehicles API Client
 * Strapi integration for vehicles management
 */

import { getHttpClient } from '@ufc/core'
import type {
  Vehicle,
  VehiclesFilter,
  PaginatedVehicles,
  PaginationMeta,
} from '../../domain/types'
import { DEFAULT_PAGE_SIZE } from '../../domain/types'
import {
  STRAPI_ASSET_ENDPOINTS,
  type StrapiAssetListResponse,
  type StrapiAssetResponse,
  type StrapiAssetTypeListResponse,
  strapiAssetListResponseSchema,
  strapiAssetResponseSchema,
  strapiAssetTypeListResponseSchema,
  type EntityUpdateResult,
} from '../../api/api-types'
import {
  transformStrapiAssetToVehicle,
  transformStrapiAssetsToVehicles,
  transformVehicleToStrapiCreate,
  transformVehicleToStrapiUpdate,
} from '../../api/transformers'

// ============================================================================
// Default Populate Options
// ============================================================================

const DEFAULT_ASSET_POPULATE = {
  'populate[tenant]': '*',
  // Note: We don't populate assetType to avoid relation issues
}

// ============================================================================
// Helper: Deep clean request data to remove relation fields
// ============================================================================

function deepCleanRequestData(data: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {}
  
  // Only include scalar fields and metadata (no relations)
  // NOTE: 'type' is a relation field, so we handle it specially to avoid sending nested objects
  const allowedScalarFields = [
    'assetNumber', 'make', 'model', 'year', 'vin', 'serialNumber',
    'licensePlate', 'assetStatus', 'capacity', 'capacityUnit', 'description',
  ]
  
  // Handle scalar fields
  allowedScalarFields.forEach(field => {
    if (field in data && data[field] !== undefined) {
      const value = data[field]
      // Only include primitives, null, or arrays of primitives
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        cleaned[field] = value
      }
    }
  })
  
  // Handle 'type' field specially - it's a relation, so only accept scalar values (documentId string)
  // If it's an object (like { parent: {...} }), extract documentId or exclude it
  if ('type' in data && data.type !== undefined) {
    const typeValue = data.type
    if (typeValue === null || typeof typeValue === 'string') {
      // Accept null or string (documentId)
      cleaned.type = typeValue
    } else if (typeof typeValue === 'object' && 'documentId' in typeValue) {
      // Extract documentId from relation object
      cleaned.type = (typeValue as { documentId: string }).documentId
    } else {
      // Exclude if it's an object without documentId (like { parent: {...} })
      console.warn('[VehiclesAPI] Warning: type field is an object without documentId, excluding it:', typeValue)
    }
  }
  
  // Handle metadata separately - clean it to remove any nested objects that might be relations
  if (data.metadata && typeof data.metadata === 'object' && !Array.isArray(data.metadata)) {
    const metadata = data.metadata as Record<string, unknown>
    const cleanMetadata: Record<string, unknown> = {}
    
    Object.keys(metadata).forEach(key => {
      const value = metadata[key]
      // Only include primitives or simple objects (like capacity: {value, unit})
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        cleanMetadata[key] = value
      } else if (key === 'capacity' && typeof value === 'object' && 'value' in value && 'unit' in value) {
        // Allow capacity object - but clean it to ensure no nested relations
        const capacityObj = value as Record<string, unknown>
        cleanMetadata[key] = {
          value: capacityObj.value,
          unit: capacityObj.unit,
        }
      } else if (key === 'type' && typeof value === 'string') {
        // Allow type string
        cleanMetadata[key] = value
      }
      // Exclude any other objects (they might be relations like assetType)
    })
    
    if (Object.keys(cleanMetadata).length > 0) {
      cleaned.metadata = cleanMetadata
    }
  }
  
  // CRITICAL: Explicitly exclude ALL relation fields - check both top level and nested
  const relationFields = ['assetType', 'location', 'assignedDriver', 'insurances', 'permits', 'registrations', 'tenant']
  relationFields.forEach(field => {
    delete cleaned[field]
    // Also check if it exists in the original data and log a warning
    if (field in data && data[field] !== undefined) {
      console.warn(`[VehiclesAPI] Warning: Relation field '${field}' was included in request data and has been removed`)
    }
  })
  
  return cleaned
}

// ============================================================================
// Vehicles API
// ============================================================================

export const vehiclesApi = {
  /**
   * Get all vehicles (assets with type='vehicle' or category='VEHICLE')
   */
  async getVehicles(filter?: VehiclesFilter): Promise<Vehicle[]> {
    const http = getHttpClient()
    const params = new URLSearchParams()
    
    // Filter by asset type relation (vehicle)
    // Since type is now a relation, filter by the code field of the relation
    params.set('filters[type][code][$eq]', 'vehicle')

    if (filter?.page) {
      params.set('pagination[page]', String(filter.page))
    }
    if (filter?.pageSize) {
      params.set('pagination[pageSize]', String(filter.pageSize))
    }
    
    // Add status filter if provided
    if (filter?.status && filter.status !== 'all') {
      if (Array.isArray(filter.status)) {
        // Handle multiple statuses
        filter.status.forEach((status, index) => {
          const statusMap: Record<string, string> = {
            available: 'active',
            assigned: 'active',
            maintenance: 'maintenance',
            'out-of-service': 'inactive',
          }
          const backendStatus = statusMap[status] || 'active'
          params.set(`filters[$or][${index}][assetStatus][$eq]`, backendStatus)
        })
      } else {
        const statusMap: Record<string, string> = {
          available: 'active',
          assigned: 'active',
          maintenance: 'maintenance',
          'out-of-service': 'inactive',
        }
        params.set('filters[assetStatus][$eq]', statusMap[filter.status] || 'active')
      }
    }

    if (filter?.type && filter.type !== 'all') {
      // Handle both single value and array
      // Note: vehicleType is stored in metadata JSON field
      if (Array.isArray(filter.type)) {
        filter.type.forEach((type, index) => {
          params.set(`filters[$or][${index}][metadata][vehicleType][$eq]`, type)
        })
      } else {
        params.set('filters[metadata][vehicleType][$eq]', filter.type)
      }
    }

    // Add ownership filter if provided
    if (filter?.ownership && filter.ownership !== 'all') {
      // Handle both single value and array
      if (Array.isArray(filter.ownership)) {
        filter.ownership.forEach((ownership, index) => {
          params.set(`filters[$or][${index}][ownership][$eq]`, ownership)
        })
      } else {
        params.set('filters[ownership][$eq]', filter.ownership)
      }
    }

    // License plate filter (search by plate number)
    if (filter?.licensePlate) {
      params.set('filters[licensePlate][$containsi]', filter.licensePlate)
    }

    // Binded filter (whether vehicle is assigned to a resource)
    if (filter?.isBinded !== undefined && filter.isBinded !== 'all') {
      if (filter.isBinded === true) {
        params.set('filters[resources][$notNull]', 'true')
      } else {
        params.set('filters[resources][$null]', 'true')
      }
    }

    // Add search filter if provided - ONLY search by licensePlate
    if (filter?.search) {
      params.set('filters[licensePlate][$containsi]', filter.search)
    }
    
    // Add populate params
    Object.entries(DEFAULT_ASSET_POPULATE).forEach(([key, value]) => {
      params.set(key, value)
    })
    
    const url = `${STRAPI_ASSET_ENDPOINTS.LIST}?${params.toString()}`
    const response = await http.get<StrapiAssetListResponse>(url)
    
    // Handle response validation more gracefully
    try {
      const validated = strapiAssetListResponseSchema.parse(response.data)
      return transformStrapiAssetsToVehicles(validated.data)
    } catch (error) {
      console.error('Strapi response validation error:', error)
      console.error('Response data:', response.data)
      // Try to transform anyway if we have data
      if (response.data?.data && Array.isArray(response.data.data)) {
        return transformStrapiAssetsToVehicles(response.data.data)
      }
      throw error
    }
  },
  
  /**
   * Get vehicles with pagination
   */
  async getVehiclesPaginated(filter?: VehiclesFilter): Promise<PaginatedVehicles> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    // Pagination
    const page = filter?.page ?? 1
    const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE
    params.set('pagination[page]', String(page))
    params.set('pagination[pageSize]', String(pageSize))

    // Filter by asset type relation (vehicle)
    params.set('filters[type][code][$eq]', 'vehicle')

    // Status filter - supports single value or array
    const statusMap: Record<string, string> = {
      available: 'active',
      assigned: 'active',
      maintenance: 'maintenance',
      'out-of-service': 'inactive',
    }
    if (filter?.status && filter.status !== 'all') {
      if (Array.isArray(filter.status)) {
        // Multiple statuses - use $in operator
        filter.status.forEach((status, index) => {
          params.set(`filters[assetStatus][$in][${index}]`, statusMap[status] || 'active')
        })
      } else {
        // Single status
        params.set('filters[assetStatus][$eq]', statusMap[filter.status] || 'active')
      }
    }

    // Type filter (mixer/flatbed) - stored in metadata.vehicleType
    // Note: vehicleType is stored in metadata JSON field, not in the type relation
    if (filter?.type && filter.type !== 'all') {
      if (Array.isArray(filter.type)) {
        // Multiple types - use $or with multiple conditions
        filter.type.forEach((type, index) => {
          params.set(`filters[$or][${index}][metadata][vehicleType][$eq]`, type)
        })
      } else {
        // Single type
        params.set('filters[metadata][vehicleType][$eq]', filter.type)
      }
    }

    // Ownership filter - supports single value or array
    const ownershipMap: Record<string, string> = {
      owned: 'OWNED',
      leased: 'LEASED_IN',
      'rented-out': 'RENTED_OUT',
      outsourced: 'OUTSOURCED',
      brokered: 'BROKERED',
      shared: 'SHARED',
    }
    if (filter?.ownership && filter.ownership !== 'all') {
      if (Array.isArray(filter.ownership)) {
        // Multiple ownership types - use $in operator
        filter.ownership.forEach((ownership, index) => {
          params.set(`filters[ownershipType][$in][${index}]`, ownershipMap[ownership] || ownership)
        })
      } else {
        // Single ownership type
        params.set('filters[ownershipType][$eq]', ownershipMap[filter.ownership] || filter.ownership)
      }
    }

    // License plate filter (search by plate number)
    if (filter?.licensePlate) {
      params.set('filters[licensePlate][$containsi]', filter.licensePlate)
    }

    // Binded filter (whether vehicle is assigned to a resource)
    if (filter?.isBinded !== undefined && filter.isBinded !== 'all') {
      if (filter.isBinded === true) {
        // Vehicle is binded - has a resource assignment
        params.set('filters[resources][$notNull]', 'true')
      } else {
        // Vehicle is not binded - has no resource assignment
        params.set('filters[resources][$null]', 'true')
      }
    }

    // Add search filter if provided - ONLY search by licensePlate
    if (filter?.search) {
      params.set('filters[licensePlate][$containsi]', filter.search)
    }

    // Sorting - map frontend field names to Strapi field names
    if (filter?.sortBy) {
      const sortFieldMap: Record<string, string> = {
        plateNumber: 'licensePlate',
        make: 'make',
        capacity: 'capacity',
        status: 'assetStatus',
        ownership: 'ownershipType',
        createdAt: 'createdAt',
      }
      const strapiField = sortFieldMap[filter.sortBy] || filter.sortBy
      // Strapi currently returns capacity sort in reverse direction for this field,
      // so normalize it here to match the UI's expected asc/desc behavior.
      const isCapacitySort = filter.sortBy === 'capacity'
      const sortDirection = isCapacitySort
        ? (filter.sortOrder === 'desc' ? ':asc' : ':desc')
        : (filter.sortOrder === 'desc' ? ':desc' : ':asc')
      params.set('sort[0]', `${strapiField}${sortDirection}`)
    } else {
      // Default sort by plate number ascending
      params.set('sort[0]', 'licensePlate:asc')
    }

    // Add populate params
    Object.entries(DEFAULT_ASSET_POPULATE).forEach(([key, value]) => {
      params.set(key, value)
    })

    const url = `${STRAPI_ASSET_ENDPOINTS.LIST}?${params.toString()}`
    const response = await http.get<StrapiAssetListResponse>(url)

    // Extract pagination from response
    const meta = response.data?.meta?.pagination
    const pagination: PaginationMeta = {
      page: meta?.page ?? page,
      pageSize: meta?.pageSize ?? pageSize,
      pageCount: meta?.pageCount ?? 1,
      total: meta?.total ?? 0,
    }

    // Handle response validation
    try {
      const validated = strapiAssetListResponseSchema.parse(response.data)
      return {
        data: transformStrapiAssetsToVehicles(validated.data),
        pagination,
      }
    } catch (error) {
      console.error('Strapi response validation error:', error)
      if (response.data?.data && Array.isArray(response.data.data)) {
        return {
          data: transformStrapiAssetsToVehicles(response.data.data),
          pagination,
        }
      }
      throw error
    }
  },

  /**
   * Get a single vehicle by documentId
   */
  async getVehicle(documentId: string): Promise<Vehicle | null> {
    const http = getHttpClient()
    const params = new URLSearchParams()
    Object.entries(DEFAULT_ASSET_POPULATE).forEach(([key, value]) => {
      params.set(key, value)
    })
    
    const url = `${STRAPI_ASSET_ENDPOINTS.DETAIL(documentId)}?${params.toString()}`
    const response = await http.get<StrapiAssetResponse>(url)
    const validated = strapiAssetResponseSchema.parse(response.data)
    return transformStrapiAssetToVehicle(validated.data)
  },
  
  /**
   * Create a new vehicle
   */
  async createVehicle(vehicle: Partial<Vehicle> & { tenantId: string }): Promise<Vehicle> {
    const http = getHttpClient()
    
    // Fetch the asset type documentId for 'vehicle' code - REQUIRED
    // This page is only for vehicles, so we must always have the vehicle type documentId
    let typeDocumentId: string | undefined
    try {
      const params = new URLSearchParams()
      params.set('filters[code][$eq]', 'vehicle')
      const url = `${STRAPI_ASSET_ENDPOINTS.ASSET_TYPES}?${params.toString()}`
      const response = await http.get<StrapiAssetTypeListResponse>(url)
      const validated = strapiAssetTypeListResponseSchema.parse(response.data)
      if (validated.data && validated.data.length > 0) {
        typeDocumentId = validated.data[0].documentId
      } else {
        throw new Error('Vehicle asset type not found in Strapi. Please ensure the asset type with code "vehicle" exists.')
      }
    } catch (error) {
      console.error('Failed to fetch asset type for vehicle:', error)
      throw new Error(`Cannot create vehicle: Failed to fetch vehicle asset type. ${error instanceof Error ? error.message : String(error)}`)
    }
    
    if (!typeDocumentId) {
      throw new Error('Cannot create vehicle: Vehicle asset type documentId is missing.')
    }
    
    const requestData = transformVehicleToStrapiCreate(vehicle, typeDocumentId)
    
    // Deep clean the data object to remove any relation fields with nested data
    if (requestData.data && typeof requestData.data === 'object') {
      let cleaned = deepCleanRequestData(requestData.data as Record<string, unknown>)
      
      // Final safety: JSON serialize/parse to remove any hidden nested objects
      try {
        const serialized = JSON.stringify(cleaned)
        const parsed = JSON.parse(serialized) as Record<string, unknown>
        
        // One final pass - remove any relation fields that might have survived
        const finalClean: Record<string, unknown> = {}
        const allowedFields = ['assetNumber', 'type', 'make', 'model', 'year', 'vin', 'serialNumber', 'licensePlate', 'assetStatus', 'capacity', 'capacityUnit', 'description', 'metadata', 'ownershipType']
        
        Object.keys(parsed).forEach(key => {
          if (allowedFields.includes(key)) {
            const value = parsed[key]
            // Special handling for 'type' - ensure it's only a scalar (documentId string or null)
            if (key === 'type') {
              if (value === null || typeof value === 'string') {
                finalClean[key] = value
              } else if (typeof value === 'object' && value !== null && 'documentId' in value) {
                // Extract documentId if it's an object
                finalClean[key] = (value as { documentId: string }).documentId
              }
              // Otherwise exclude it (it's a nested relation object)
            } else {
              finalClean[key] = value
            }
          }
        })
        
        requestData.data = finalClean
      } catch (e) {
        // If serialization fails, use cleaned data as-is but still remove tenant and relations
        console.error('JSON serialization/parsing failed during createVehicle cleanup:', e)
        delete cleaned.tenant
        delete cleaned.assetType
        requestData.data = cleaned
      }
      
      // Absolute final check - ensure tenant and relation fields are definitely not there
      const finalData = requestData.data as Record<string, unknown>
      delete finalData.tenant
      delete finalData.assetType
      delete finalData.location
      delete finalData.assignedDriver
      
      // CRITICAL: Ensure 'type' is only a scalar (documentId string or null), not an object
      if ('type' in finalData) {
        const typeValue = finalData.type
        if (typeValue === null || typeof typeValue === 'string') {
          // Good - it's already a scalar
          console.log('[VehiclesAPI] Type field is scalar (documentId):', typeValue)
        } else if (typeof typeValue === 'object' && typeValue !== null) {
          // It's an object - extract documentId or remove it
          if ('documentId' in typeValue) {
            finalData.type = (typeValue as { documentId: string }).documentId
            console.log('[VehiclesAPI] Extracted documentId from type object:', finalData.type)
          } else {
            // If it's an object without documentId (like { parent: {...} }), remove it
            console.warn('[VehiclesAPI] Removing invalid type object (has nested properties):', JSON.stringify(typeValue))
            delete finalData.type
          }
        }
      } else {
        console.warn('[VehiclesAPI] WARNING: type field is missing from request data!')
      }
    }
    
    const response = await http.post(
      STRAPI_ASSET_ENDPOINTS.CREATE,
      requestData
    )

    // Be very defensive with response shape – avoid hard failing on Zod here
    try {
      // If the response matches the usual Strapi shape { data, meta }
      const respData: any = (response as any).data
      if (respData && respData.data) {
        // Try Zod validation first
        try {
          const validated = strapiAssetResponseSchema.parse(respData)
          return transformStrapiAssetToVehicle(validated.data)
        } catch (zodError) {
          console.error('Strapi response validation error (vehicles/create):', zodError)
          console.error('Response data:', respData)
          // Fallback: transform raw data without schema
          return transformStrapiAssetToVehicle(respData.data)
        }
      }

      // Fallback: if backend returns the DTO directly instead of wrapped in { data }
      if (respData && respData.id && respData.documentId) {
        return transformStrapiAssetToVehicle(respData)
      }

      throw new Error('Unexpected createVehicle response shape')
    } catch (error) {
      console.error('Failed to handle createVehicle response:', error)
      console.error('Raw response:', response)
      throw error
    }
  },
  
  /**
   * Update an existing vehicle
   */
  async updateVehicle(documentId: string, vehicle: Partial<Vehicle>): Promise<EntityUpdateResult<Vehicle>> {
    const http = getHttpClient()
    const requestData = transformVehicleToStrapiUpdate(vehicle)
    
    // Deep clean the data object to remove any relation fields with nested data
    if (requestData.data && typeof requestData.data === 'object') {
      const cleaned = deepCleanRequestData(requestData.data as Record<string, unknown>)
      
      // For updates, also remove fields that shouldn't be updated
      delete cleaned.assetNumber
      delete cleaned.type
      delete cleaned.tenant
      
      // Final safety: JSON serialize/parse to remove any hidden nested objects
      try {
        const serialized = JSON.stringify(cleaned)
        requestData.data = JSON.parse(serialized) as Record<string, unknown>
      } catch (e) {
        // If serialization fails, use cleaned data as-is
        console.error('JSON serialization/parsing failed during updateVehicle cleanup:', e)
        requestData.data = cleaned
      }
      
      // One more explicit check - ensure assetType and all relations are definitely not there
      const finalData = requestData.data as Record<string, unknown>
      delete finalData.assetType
      delete finalData.location
      delete finalData.assignedDriver
      delete finalData.insurances
      delete finalData.permits
      delete finalData.registrations
    }
    
    const response = await http.put<StrapiAssetResponse>(
      STRAPI_ASSET_ENDPOINTS.UPDATE(documentId),
      requestData
    )

    const rawData: any = response?.data
    const deactivation = {
      affectedOrders: (rawData?.affectedOrders as string[]) ?? [],
      affectedResources: (rawData?.affectedResources as string[]) ?? [],
    }

    // Handle response validation more gracefully
    let entity: Vehicle
    try {
      if (!response || !response.data) {
        throw new Error('Empty response from server')
      }

      if (rawData && 'id' in rawData && 'documentId' in rawData) {
        entity = transformStrapiAssetToVehicle(rawData as any)
      } else {
        const validated = strapiAssetResponseSchema.parse(rawData)
        entity = transformStrapiAssetToVehicle(validated.data)
      }
    } catch (error) {
      console.error('Strapi update response validation error:', error)
      console.error('Response:', response)
      console.error('Response data:', response?.data)

      if (response?.data) {
        if ('id' in response.data && 'documentId' in response.data) {
          entity = transformStrapiAssetToVehicle(response.data as any)
        } else if ((response.data as any).data && 'id' in (response.data as any).data) {
          entity = transformStrapiAssetToVehicle((response.data as any).data)
        } else {
          const errorMessage = error instanceof Error ? error.message : String(error)
          throw new Error(`Failed to update vehicle: ${errorMessage}`)
        }
      } else {
        const errorMessage = error instanceof Error ? error.message : String(error)
        throw new Error(`Failed to update vehicle: ${errorMessage}`)
      }
    }

    return { entity, deactivation }
  },
  
  /**
   * Delete a vehicle
   */
  async deleteVehicle(documentId: string): Promise<void> {
    const http = getHttpClient()
    await http.delete(STRAPI_ASSET_ENDPOINTS.UPDATE(documentId))
  },

  /**
   * Export all vehicles matching current filters (no pagination limit)
   */
  async exportAll(filter?: VehiclesFilter): Promise<Vehicle[]> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    if (filter?.status && filter.status !== 'all') params.set('status', filter.status)
    if (filter?.search) params.set('search', filter.search)
    if (filter?.type && filter.type !== 'all') params.set('type', filter.type)

    const response = await http.get<unknown>(`${STRAPI_ASSET_ENDPOINTS.LIST}/export?${params}`)
    const responseData = response.data as Record<string, unknown>
    const raw = Array.isArray(responseData?.data) ? responseData.data : []
    return transformStrapiAssetsToVehicles(raw as Parameters<typeof transformStrapiAssetsToVehicles>[0])
  },

  async bulkImport(rows: Array<Record<string, string>>): Promise<{ created: number; skipped: number; errors: string[] }> {
    const http = getHttpClient()
    const response = await http.post<unknown>(STRAPI_ASSET_ENDPOINTS.BULK_IMPORT, { data: rows })
    return response.data as { created: number; skipped: number; errors: string[] }
  },
}
